import { useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { PlusCircleIcon, PencilIcon } from '@phosphor-icons/react'
import Select from 'react-select'
import { useTranslation } from 'react-i18next'

import { useGetBuildingStaff } from '@/api/building'
import { useCreateCustomChatInbox, useUpdateCustomChatInbox } from '@/api/chat'
import { getApiErrorMessage } from '@/utils'

import ModalContainer from '../../ModalContainer/ModelContainer'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'

const InboxChatFormModal = ({ isOpen, onClose, buildingId, onAdd, chatInboxes, inbox }) => {
  const { t } = useTranslation(['chat'])
  const [name, setName] = useState('')
  const [selectedStaff, setSelectedStaff] = useState([])
  const [errors, setErrors] = useState({})

  const {
    mutate: createMutate,
    isPending: isCreatePending,
    error: createError,
    isError: isCreateError,
  } = useCreateCustomChatInbox()

  const {
    mutate: updateMutate,
    isPending: isUpdatePending,
    error: updateError,
    isError: isUpdateError,
  } = useUpdateCustomChatInbox()

  const isEditing = !!inbox
  const isPending = isCreatePending || isUpdatePending
  const isError = isCreateError || isUpdateError
  const error = createError || updateError

  useEffect(() => {
    if (inbox) {
      setName(inbox.name || '')
      // NOTE: we intentionally do not want to take admins into account
      setSelectedStaff(
        inbox.members.filter((user) => user.role !== 'Administrator').map((m) => m.id),
      )
    } else {
      setName('')
      setSelectedStaff([])
    }
    setErrors({})
  }, [inbox, isOpen])

  const { data: staff, isLoading: isLoadingStaff } = useGetBuildingStaff({
    buildingId,
    types: ['propertyManager', 'shiftManager', 'staff'],
  })

  const staffOptions = useMemo(() => {
    return staff.map((s) => ({
      value: s.user.id,
      label: `${s.user.firstName} ${s.user.lastName}`,
    }))
  }, [staff])

  const validate = () => {
    const newErrors = {}
    const trimmedName = name.trim()
    if (!trimmedName) {
      newErrors.name = t('chat:inboxChatFormModal.nameRequired')
    } else if (trimmedName.length < 2 || trimmedName.length > 50) {
      newErrors.name = t('chat:inboxChatFormModal.nameLengthError')
    } else if (!isEditing && chatInboxes.filter((ci) => ci.type === 'CUST').length >= 10) {
      newErrors.name = t('chat:inboxChatFormModal.maxCustomInboxes')
    } else if (
      chatInboxes.some((ci) => ci.name === trimmedName && (!isEditing || ci.id !== inbox.id))
    ) {
      newErrors.name = t('chat:inboxChatFormModal.nameExists')
    }

    if (selectedStaff.length === 0) {
      newErrors.staff = t('chat:inboxChatFormModal.staffRequired')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!validate()) return

    const payload = {
      name: name.trim(),
      memberIds: selectedStaff,
    }

    const options = {
      onSuccess: () => {
        onAdd()
        onClose()
      },
    }

    if (isEditing) {
      updateMutate({ id: inbox.id, ...payload }, options)
    } else {
      createMutate({ buildingId, ...payload }, options)
    }
  }

  if (!isOpen) return null

  return (
    <ModalContainer
      isVisible={isOpen}
      headerTitle={
        isEditing ? t('chat:inboxChatFormModal.editTitle') : t('chat:inboxChatFormModal.title')
      }
      headerIcon={isEditing ? <PencilIcon /> : <PlusCircleIcon />}
      onClose={onClose}
      closeable={!isPending}
    >
      <div className="inbox-creation-modal">
        <p className="heading-small" style={{ fontWeight: 500 }}>
          {isEditing
            ? t('chat:inboxChatFormModal.updateDescription')
            : t('chat:inboxChatFormModal.createDescription')}
        </p>

        {isError && (
          <div className="notification is-danger" style={{ marginBottom: '1.2rem' }}>
            {getApiErrorMessage(error)}
          </div>
        )}

        <TextField
          label={t('chat:inboxChatFormModal.inboxNameLabel')}
          required
          placeholder={t('chat:inboxChatFormModal.inboxNamePlaceholder')}
          value={name}
          name="name"
          onChange={(e) => setName(e.target.value)}
          isError={!!errors.name}
          errorLabel={errors.name}
        />

        <div className="field">
          <label className="label">{t('chat:inboxChatFormModal.assignees')}</label>
          <div className="control select-field">
            <Select
              placeholder={t('chat:inboxChatFormModal.assignTeamMembers')}
              name="staff"
              options={staffOptions}
              value={staffOptions.filter((opt) => selectedStaff.includes(opt.value))}
              onChange={(options) =>
                setSelectedStaff(options ? options.map((opt) => opt.value) : [])
              }
              isLoading={isLoadingStaff}
              isMulti
            />
          </div>
          {!!errors.staff && <span className="label--error">{errors.staff}</span>}
        </div>

        <div className="inbox-creation-modal-footer">
          <button
            className="button button-gray"
            onClick={onClose}
            disabled={isPending}
            type="button"
          >
            {t('common:actions.close')}
          </button>
          <button
            className={`button button-primary ${isPending ? 'is-loading' : ''}`}
            onClick={handleSave}
            disabled={isPending}
            type="button"
          >
            {!isPending &&
              (isEditing ? (
                <PencilIcon size={20} style={{ marginRight: 4 }} />
              ) : (
                <PlusCircleIcon size={20} style={{ marginRight: 4 }} />
              ))}
            {isEditing
              ? t('chat:inboxChatFormModal.saveChanges')
              : t('chat:inboxChatFormModal.add')}
          </button>
        </div>
      </div>
    </ModalContainer>
  )
}

InboxChatFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  buildingId: PropTypes.number.isRequired,
  onAdd: PropTypes.func.isRequired,
  chatInboxes: PropTypes.array.isRequired,
  inbox: PropTypes.object,
}

export default InboxChatFormModal
